import React from "react";
import {
  formatDisplayDate,
  formatShortMonthDay,
  formatHours,
  getHospitalDepartments,
  getRowLabelDate,
  getTotalHourStep,
  getTotal
} from "../../utils/timecard";
import { useAutoExpandDayCard } from "../../hooks/useAutoExpandDayCard";
import { useTimeFormat } from "../../hooks/useTimeFormat";
import Field from "../common/Field";
import TimeCell from "../common/TimeCell";
import DepartmentSelect from "./DepartmentSelect";
import DayCard from "./DayCard";

export default function CallHoursSection({
  rows,
  onChange,
  hospitalOptions = [],
  weekStartDate,
  readOnly = false
}) {
  const { setDayCardRef } = useAutoExpandDayCard();
  const { timeFormat } = useTimeFormat();


  return (
    <section className="sheet-section">

<div className="table-scroll compact-scroll">
         <table className="time-entry-table compact-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Hospital</th>
              <th scope="col">Dept</th>
              <th scope="col">Start</th>
              <th scope="col">End</th>
              <th scope="col" className="hours-column">Hours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.day}>
                <th scope="row">
                  <div className="day-date-cell">
                    <span className="weekday">{row.day}</span>
                    {getRowLabelDate(row.date, weekStartDate, row.day)
                      ? <span className="date">{formatShortMonthDay(getRowLabelDate(row.date, weekStartDate, row.day))}</span>
                      : null}
                  </div>
                </th>
                <td>
                  <select
                    aria-label={`${row.day} call hospital`}
                    value={row.hospital}
                    disabled={readOnly}
                    onChange={(event) => onChange(index, "hospital", event.target.value)}
                  >
                    <option value="">Select hospital</option>
                    {hospitalOptions.map((hospital) => (
                      <option key={hospital.hospitalId} value={hospital.name}>
                        {hospital.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <DepartmentSelect
                    ariaLabel={`${row.day} call department`}
                    departments={getHospitalDepartments(hospitalOptions, row.hospital)}
                    value={row.department}
                    disabled={readOnly}
                    onChange={(value) => onChange(index, "department", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.startTime}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} call start`}
                    onChange={(value) => onChange(index, "startTime", value)}
                  />
                </td>
                <td>
                  <TimeCell
                    value={row.endTime}
                    format={timeFormat}
                    readOnly={readOnly}
                    ariaLabel={`${row.day} call end`}
                    onChange={(value) => onChange(index, "endTime", value)}
                  />
                </td>
                <td className="hours-column">
                  <input
                    type="number"
                    min="0"
                    step={getTotalHourStep(row.hospital, hospitalOptions)}
                    aria-label={`${row.day} call hours`}
                    value={row.totalHours}
                    readOnly
                    disabled={readOnly}
                    onChange={(event) =>
                      onChange(index, "totalHours", event.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan="5" scope="row">
                Total on call for the week
              </th>
              <td className="hours-column">{formatHours(getTotal(rows))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mobile-entry-list compact-entry-list" aria-label="Call hours by day">
        {rows.map((row, index) => {
          const labelDate = getRowLabelDate(row.date, weekStartDate, row.day);
          const dateLabel = labelDate ? `${row.day} — ${formatDisplayDate(labelDate)}` : row.day;
          return (
            <DayCard
              key={row.day}
              day={row.day}
              dateLabel={dateLabel}
              hospital={row.hospital}
              totalHours={row.totalHours}
              setDayCardRef={setDayCardRef}
            >
              <Field id={`call-${row.day}-hospital`} label="Hospital">
                <select
                  id={`call-${row.day}-hospital`}
                  value={row.hospital}
                  disabled={readOnly}
                  onChange={(event) => onChange(index, "hospital", event.target.value)}
                >
                  <option value="">Select hospital</option>
                  {hospitalOptions.map((hospital) => (
                    <option key={hospital.hospitalId} value={hospital.name}>
                      {hospital.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id={`call-${row.day}-dept`} label="Dept">
              <DepartmentSelect
                id={`call-${row.day}-dept`}
                ariaLabel={`${row.day} call department`}
                departments={getHospitalDepartments(hospitalOptions, row.hospital)}
                value={row.department}
                disabled={readOnly}
                onChange={(value) => onChange(index, "department", value)}
              />
              </Field>
              <Field id={`call-${row.day}-start`} label="Start time">
                <TimeCell
                  id={`call-${row.day}-start`}
                  value={row.startTime}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} call start`}
                  onChange={(value) => onChange(index, "startTime", value)}
                />
              </Field>
              <Field id={`call-${row.day}-end`} label="End time">
                <TimeCell
                  id={`call-${row.day}-end`}
                  value={row.endTime}
                  format={timeFormat}
                  readOnly={readOnly}
                  ariaLabel={`${row.day} call end`}
                  onChange={(value) => onChange(index, "endTime", value)}
                />
              </Field>
              <Field id={`call-${row.day}-hours`} label="Total hours" full>
                <input
                  id={`call-${row.day}-hours`}
                  type="number"
                  min="0"
                  step={getTotalHourStep(row.hospital, hospitalOptions)}
                  value={row.totalHours}
                  disabled={readOnly}
                  onChange={(event) =>
                    onChange(index, "totalHours", event.target.value)
                  }
                />
              </Field>
            </DayCard>
          );
        })}
      </div>
    </section>
  );
}
